/**
 * Product AI Search Integration
 * Integrates AI-powered product search via the public API
 */

class ProductAISearch {
  constructor() {
    this.cache = new Map();
    this.ttl = 5 * 60 * 1000; // 5 minutes cache TTL
    this.searchInProgress = false;
  }

  /**
   * Search for products using the AI-powered API
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @returns {Promise<Object>} Search results
   */
  async searchProducts(query, options = {}) {
    if (!query || !query.trim()) {
      return {
        results: [],
        pagination: { total: 0, page: 1, limit: 20, totalPages: 0 },
      };
    }

    const cacheKey = this.getCacheKey(query, options);

    // Check cache
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.ttl) {
        return cached.data;
      } else {
        this.cache.delete(cacheKey);
      }
    }

    const maxRetries = Number.isFinite(Number(options.retries))
      ? Math.max(0, Number(options.retries))
      : Math.max(0, Number(window.getAISearchConfig?.("retry.maxAttempts", 0) || 0));

    return this.fetchProducts(query, options, cacheKey, maxRetries);
  }

  /**
   * Fetch products from API with retry logic
   */
  async fetchProducts(query, options, cacheKey, retries = 2) {
    const timeout = Number(options.timeout || 0);
    const timeoutController = timeout ? new AbortController() : null;
    const timeoutId = timeoutController
      ? setTimeout(() => timeoutController.abort(), timeout)
      : null;
    const signal = this.combineSignals(options.signal, timeoutController?.signal);
    const storeSearchMode = options.storeSearch !== false;
    const endpoint = storeSearchMode
      ? window.getAISearchConfig?.("api.baseUrl", "https://intelligence-api.pleiades.solutions/api/store-search") ||
        "https://intelligence-api.pleiades.solutions/api/store-search"
      : `${
          window.getAISearchConfig?.(
            "api.productSearchBaseUrl",
            "https://intelligence-api.pleiades.solutions/api/product-comparison"
          ) || "https://intelligence-api.pleiades.solutions/api/product-comparison"
        }/search`;
    const params = new URLSearchParams({
      [storeSearchMode ? "q" : "query"]: query,
      vendor: options.vendor || "",
      minPrice: options.minPrice || "",
      maxPrice: options.maxPrice || "",
      sortBy: options.sortBy || "relevance",
      sortOrder: options.sortOrder || "asc",
      page: options.page || 1,
      limit: options.limit || 20,
      domain: options.domain || window.location.hostname,
      noCache: options.noCache === true ? "true" : "false",
    });

    // Remove empty parameters
    Array.from(params.keys()).forEach((key) => {
      if (!params.get(key)) {
        params.delete(key);
      }
    });

    try {
      const response = await fetch(`${endpoint}?${params}`, { signal });

      if (!response.ok) {
        if (retries > 0 && response.status >= 500) {
          // Retry on server errors
          await this.wait(Math.pow(2, 3 - retries) * 1000, options.signal);
          return this.fetchProducts(query, options, cacheKey, retries - 1);
        }
        throw new Error(`Search API error: ${response.status}`);
      }

      const data = await response.json();
      const normalizedData = this.normalizeSearchResponse(data);

      // Cache the results
      this.cache.set(cacheKey, {
        data: normalizedData,
        timestamp: Date.now(),
      });

      return normalizedData;
    } catch (error) {
      if (error?.name === "AbortError") {
        if (!options.signal?.aborted) {
          return {
            results: [],
            pagination: { total: 0, page: 1, limit: 20, totalPages: 0 },
          };
        }

        throw error;
      }

      console.error("Product AI Search error:", error);
      // Return empty results on error (graceful fallback)
      return {
        results: [],
        pagination: { total: 0, page: 1, limit: 20, totalPages: 0 },
      };
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  combineSignals(...signals) {
    const validSignals = signals.filter(Boolean);
    if (validSignals.length === 0) return undefined;
    if (validSignals.length === 1) return validSignals[0];

    const controller = new AbortController();
    const abort = () => controller.abort();

    validSignals.forEach((signal) => {
      if (signal.aborted) {
        abort();
      } else {
        signal.addEventListener("abort", abort, { once: true });
      }
    });

    return controller.signal;
  }

  normalizeSearchResponse(data) {
    if (!data?.results || !Array.isArray(data.results)) return data;

    const results = data.results
      .map((result) => {
        if (!result || typeof result !== "object") return null;

        const type = this.normalizeResultType(result.type);
        const myProductUrl = this.normalizeStoreUrl(result.myProductUrl, type);
        const url = myProductUrl || this.normalizeStoreUrl(result.url, type);

        if (!url) return null;

        return {
          ...result,
          type,
          url,
          myProductUrl: myProductUrl || url,
        };
      })
      .filter(Boolean);

    return {
      ...data,
      results,
    };
  }

  /**
   * Return the preferred, verified storefront URL for an API result.
   * Supplier URLs are never used as a fallback.
   */
  getSafeResultUrl(result) {
    if (!result || typeof result !== "object") return null;

    const type = this.normalizeResultType(result.type);
    return (
      this.normalizeStoreUrl(result.myProductUrl, type) ||
      this.normalizeStoreUrl(result.url, type)
    );
  }

  normalizeResultType(type) {
    if (typeof type !== "string") return null;

    const normalizedType = type.trim().toLowerCase();
    return ["product", "collection", "article", "page"].includes(
      normalizedType
    )
      ? normalizedType
      : null;
  }

  getExpectedPathPrefix(type) {
    return {
      product: "/products/",
      collection: "/collections/",
      article: "/blogs/",
      page: "/pages/",
    }[type];
  }

  getAllowedStoreHosts() {
    const hosts = new Set();
    const currentHost = window.location?.hostname?.toLowerCase();

    if (currentHost) hosts.add(currentHost);

    const shopifyHost = window.Shopify?.shop;
    if (
      typeof shopifyHost === "string" &&
      /^[a-z0-9][a-z0-9.-]*\.myshopify\.com$/i.test(shopifyHost.trim())
    ) {
      hosts.add(shopifyHost.trim().toLowerCase());
    }

    return hosts;
  }

  normalizeStoreUrl(url, type) {
    if (!url || typeof url !== "string") return null;

    const expectedPathPrefix = this.getExpectedPathPrefix(type);
    if (!expectedPathPrefix) return null;

    try {
      const parsedUrl = new URL(url.trim(), `${window.location.origin}/`);
      const isAllowedProtocol =
        parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
      const isAllowedHost = this.getAllowedStoreHosts().has(
        parsedUrl.hostname.toLowerCase()
      );
      const hasExpectedPath =
        parsedUrl.pathname.startsWith(expectedPathPrefix) &&
        parsedUrl.pathname.length > expectedPathPrefix.length;

      if (!isAllowedProtocol || !isAllowedHost || !hasExpectedPath) return null;

      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
      return null;
    }
  }

  /**
   * Get random products for suggestions
   */
  async getRandomProducts(limit = 10) {
    const finalLimit = Math.min(Math.max(limit, 1), 20);
    const cacheKey = `random-${finalLimit}`;

    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.ttl) {
        return cached.data;
      }
      this.cache.delete(cacheKey);
    }

    try {
      const response = await fetch(
        `https://intelligence-api.pleiades.solutions/api/product-comparison/random?limit=${finalLimit}`
      );

      if (!response.ok) {
        throw new Error(`Random products API error: ${response.status}`);
      }

      const data = await response.json();

      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });

      return data;
    } catch (error) {
      console.error("Failed to fetch random products:", error);
      return {
        results: [],
        pagination: { total: 0, page: 1, limit: 20, totalPages: 0 },
      };
    }
  }

  /**
   * Search by SKU or title
   */
  async searchBySkuOrTitle(sku = null, title = null) {
    if (!sku && !title) {
      return {
        results: [],
        pagination: { total: 0, page: 1, limit: 20, totalPages: 0 },
      };
    }

    const params = new URLSearchParams();
    if (sku) params.append("sku", sku);
    if (title) params.append("title", title);

    try {
      const response = await fetch(
        `https://intelligence-api.pleiades.solutions/api/product-comparison?${params}`
      );

      if (!response.ok) {
        throw new Error(`Search by SKU/Title error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Search by SKU/Title failed:", error);
      return {
        results: [],
        pagination: { total: 0, page: 1, limit: 20, totalPages: 0 },
      };
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Helper to generate cache key
   */
  getCacheKey(query, options) {
    return JSON.stringify({
      query: query.toLowerCase().trim(),
      storeSearch: options.storeSearch !== false,
      domain: options.domain || window.location.hostname,
      vendor: options.vendor || "",
      minPrice: options.minPrice || "",
      maxPrice: options.maxPrice || "",
      sortBy: options.sortBy || "relevance",
      sortOrder: options.sortOrder || "asc",
      page: options.page || 1,
      limit: options.limit || 20,
    });
  }

  /**
   * Helper to wait (for retries)
   */
  wait(ms, signal) {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }

      const timeoutId = setTimeout(resolve, ms);

      signal?.addEventListener(
        "abort",
        () => {
          clearTimeout(timeoutId);
          reject(new DOMException("Aborted", "AbortError"));
        },
        { once: true }
      );
    });
  }
}

// Create global instance
window.productAISearch = new ProductAISearch();
